/* ============================================================
   แท็บ "ขั้นตอนการผลิต" ของ Production Order
   แสดง Master Process ของสินค้าแต่ละรายการใน PD เรียงตามขั้นตอน
   (อ่านอย่างเดียว ใช้ดูหน้างานว่าแต่ละกลุ่มต้องเตรียม/ผสมอะไรบ้าง)
============================================================ */
import { useEffect, useState } from "react";
import { Card, Table, Tag, Empty, Collapse, Typography, Spin, Alert } from "antd";

import ProductionOrderService from "../../../service/ERP/ProductionOrder.service";

const pdService = ProductionOrderService();

const num3 = (v) =>
  Number(v || 0) > 0
    ? Number(v).toLocaleString("en-US", {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      })
    : "-";

/* remark บางสูตรขึ้นต้นด้วยลำดับขั้นตอนหรือขีดคั่นมาด้วย เช่น "-1 เทผสมรวมกัน"
   ตัดส่วนนั้นทิ้งเพื่อไม่ให้ซ้ำกับคำว่า "กลุ่มที่ N" ที่แสดงอยู่แล้ว */
const cleanRemark = (remark, stepno) => {
  let t = String(remark || "").trim();
  t = t.replace(/^[-–—.\s]+/, "");
  const sn = String(stepno ?? "").trim();
  if (sn && t.startsWith(sn)) t = t.slice(sn.length);
  return t.replace(/^[-–—.)\s]+/, "").trim();
};

const columns = [
  { title: "ลำดับ", width: 70, align: "center", render: (_v, _r, i) => i + 1 },
  { title: "รหัสวัตถุดิบ/ส่วนผสม", dataIndex: "mat_code", width: 170 },
  { title: "รายการ", dataIndex: "mat_name" },
  {
    title: "ปริมาณ/ชุด",
    dataIndex: "value",
    width: 130,
    align: "right",
    render: (v) => num3(v),
  },
  { title: "หน่วย", dataIndex: "unit", width: 90, align: "center" },
  {
    title: "วิธีการ",
    dataIndex: "method",
    width: 220,
    render: (v) => v || "-",
  },
];

export default function ProcessStepsPanel({ pdcode }) {
  const [loading, setLoading] = useState(false);
  const [forms, setForms] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!pdcode) return;
    setLoading(true);
    setError("");
    pdService
      .curingRoom(pdcode, { ignoreLoading: true })
      .then((res) => setForms(res.data?.data?.forms || []))
      .catch((err) =>
        setError(err.response?.data?.message || "โหลดขั้นตอนการผลิตไม่สำเร็จ")
      )
      .finally(() => setLoading(false));
  }, [pdcode]);

  if (!pdcode) {
    return <Empty description="บันทึก Production Order ก่อน จึงจะดูขั้นตอนการผลิตได้" />;
  }
  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <Spin />
      </div>
    );
  }
  if (error) return <Alert type="error" showIcon message={error} />;
  if (forms.length === 0) return <Empty description="ไม่พบข้อมูล" />;

  return (
    <div className="flex flex-col gap-3">
      {forms.map((f, fi) => (
        <Card
          key={`${f.fg_code}-${fi}`}
          size="small"
          title={
            <span>
              {f.fg_name || f.pg_name || "-"}{" "}
              {f.product_no && <Tag color="blue">{f.product_no}</Tag>}
              {f.jocode && <Tag>JO : {f.jocode}</Tag>}
              <Tag color="geekblue">{f.batch_count || 0} ชุด</Tag>
            </span>
          }
        >
          {(f.steps || []).length === 0 ? (
            <Empty description="FG นี้ยังไม่มี Master Process" />
          ) : (
            <Collapse
              defaultActiveKey={(f.steps || []).map((_s, i) => String(i))}
              items={(f.steps || []).map((s, si) => ({
                key: String(si),
                label: (
                  <Typography.Text strong>
                    กลุ่มที่ {s.stepno}
                    {cleanRemark(s.remark, s.stepno)
                      ? ` — ${cleanRemark(s.remark, s.stepno)}`
                      : ""}
                  </Typography.Text>
                ),
                children: (
                  <Table
                    size="small"
                    rowKey={(r, i) => `${r.mat_code}-${i}`}
                    columns={columns}
                    dataSource={s.items || []}
                    pagination={false}
                    scroll={{ x: "max-content" }}
                  />
                ),
              }))}
            />
          )}
        </Card>
      ))}
    </div>
  );
}
